import { useState, useEffect } from 'react';
import { useCafeteriaSelection } from '../../../context/CafeteriaSelectionContext';
import { productService, imageService } from '../../../services/api';
import { Product, UsageFrequency } from '../../../types/place';
import CreateProductModal from '../../../components/products/CreateProductModal';
import EditProductModal from '../../../components/products/EditProductModal';
import DeleteProductModal from '../../../components/products/DeleteProductModal';



const ProductsPage = () => {
  const { currentCafeteria } = useCafeteriaSelection();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);


  const usageFrequencyOptions = [
    { value: UsageFrequency.Unlimited, label: 'Ilimitado (Usuário pode usar quantas vezes quiser)' },
    { value: UsageFrequency.Daily, label: 'Diário (Pode usar uma vez ao dia)' },
    { value: UsageFrequency.Weekly, label: 'Semanal (Uma vez a cada 7 dias)' },
    { value: UsageFrequency.Biweekly, label: 'Quinzenal (Uma vez a cada 15 dias)' },
    { value: UsageFrequency.Monthly, label: 'Mensal (Uma vez a cada 30 dias)' }
  ];

  const getUsageFrequencyLabel = (frequency: UsageFrequency): string => {
    const option = usageFrequencyOptions.find(opt => opt.value === frequency);
    return option ? option.label.split(' (')[0] : 'Não definido';
  };

  useEffect(() => {
    if (currentCafeteria) {
      loadProducts();
    }
  }, [currentCafeteria]);

  const loadProducts = async () => {
    if (!currentCafeteria) return;

    try {
      setIsLoading(true);
      setError('');
      const data = await productService.getProducts(currentCafeteria.id);
      console.log(data)
      setProducts(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar produtos');
    } finally {
      setIsLoading(false);
    }
  };

  // This function is used in the backend calculation, keeping it for reference
  // const calculateDiscountPercentage = (original: number, discounted: number): number => {
  //   return Math.round(((original - discounted) / original) * 100);
  // };

  // Temporariamente comentado - não utilizado
  /*
  const calculateDiscountedPrice = (original: number, percentage: number): number => {
    return original * (1 - percentage / 100);
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const handleOriginalPriceChange = (value: string) => {
    const originalPrice = parseFloat(value.replace(',', '.'));
    const discountPercentage = parseFloat(createForm.discountPercentage.replace(',', '.'));
    
    if (!isNaN(originalPrice) && !isNaN(discountPercentage)) {
      const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      setCreateForm(prev => ({
        ...prev,
        originalPrice: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      }));
    } else {
      setCreateForm(prev => ({ ...prev, originalPrice: value }));
    }
  };

  const handleDiscountPercentageChange = (value: string) => {
    const percentage = parseFloat(value.replace(',', '.'));
    const originalPrice = parseFloat(createForm.originalPrice.replace(',', '.'));
    
    if (!isNaN(percentage) && !isNaN(originalPrice)) {
      const discountedPrice = calculateDiscountedPrice(originalPrice, percentage);
      setCreateForm(prev => ({
        ...prev,
        discountPercentage: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      }));
    } else {
      setCreateForm(prev => ({ ...prev, discountPercentage: value }));
    }
  };
  */

  const handleCreateProduct = async (form: any) => {
    if (!currentCafeteria) return;

    try {
      setIsCreating(true);
      setError('');

      // Verificar se discountPercentage é válido
      if (!form.discountPercentage || form.discountPercentage === '') {
        console.error('ProductsPage - discountPercentage is empty in create:', form.discountPercentage);
        setError('Percentual de desconto é obrigatório');
        return;
      }

      // Preparar dados para envio
      const productData: any = {
        name: form.name,
        description: form.description,
        originalPrice: parseFloat(form.originalPrice.replace(',', '.')),
        discountPercentage: parseFloat(form.discountPercentage),
        discountedPrice: parseFloat(form.discountedPrice.replace(',', '.')), // Adicionar o preço com desconto
        category: form.category,
        usageFrequency: form.usageFrequency,
        hasTimeRestriction: form.hasTimeRestriction,
        availableFrom: form.hasTimeRestriction ? form.availableFrom : undefined,
        availableTo: form.hasTimeRestriction ? form.availableTo : undefined,
      };

      // Verificar se a conversão foi bem-sucedida
      if (isNaN(productData.discountPercentage)) {
        console.error('ProductsPage - Failed to parse discountPercentage in create:', form.discountPercentage);
        setError('Percentual de desconto inválido');
        return;
      }

      // Verificar se o preço com desconto foi calculado corretamente
      if (isNaN(productData.discountedPrice)) {
        console.error('ProductsPage - Failed to parse discountedPrice in create:', form.discountedPrice);
        setError('Preço com desconto inválido');
        return;
      }

      // Verificação final antes da chamada da API
      if (productData.discountPercentage < 0 || productData.discountPercentage > 100) {
        console.error('ProductsPage - discountPercentage out of range in create:', productData.discountPercentage);
        setError('Percentual de desconto deve ser entre 0 e 100');
        return;
      }

      console.log('ProductsPage - All validations passed in create, calling API with:', {
        discountPercentage: productData.discountPercentage,
        discountedPrice: productData.discountedPrice
      });

      // Se há imagem, fazer upload primeiro
      if (form.imageFile) {
        const formData = new FormData();
        formData.append('file', form.imageFile);
        const response = await imageService.uploadImage(formData);
        productData.imageUrl = response.url;
      }

      await productService.createDiscountedProduct(currentCafeteria.id, productData);
      
      // Recarregar produtos
      await loadProducts();
      
      // Fechar modal
      setShowCreateModal(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar produto');
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  // Temporariamente comentado - não utilizado
  /*
  const handleInputChange = (field: keyof CreateProductForm, value: string | boolean) => {    
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditInputChange = (field: keyof EditProductForm, value: string | boolean) => {  
    setEditForm(prev => ({ ...prev, [field]: value }));
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const handleImageUpload = async (file: File, isEdit: boolean = false) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await imageService.uploadImage(formData);
      const imageUrl = response.url;
      if (isEdit) {
        setEditForm(prev => ({ ...prev, imageUrl }));
      } else {
        setCreateForm(prev => ({ ...prev, imageUrl }));
      }
      return imageUrl;
    } catch (err: any) {
      setError('Erro ao fazer upload da imagem');
      throw err;
    }
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB');
        return;
      }

      if (isEdit) {
        setEditForm(prev => ({ ...prev, imageFile: file }));
      } else {
        setCreateForm(prev => ({ ...prev, imageFile: file }));
      }
    }
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const removeImage = (isEdit: boolean = false) => {
    if (isEdit) {
      setEditForm(prev => ({ ...prev, imageFile: undefined, imageUrl: undefined }));
    } else {
      setCreateForm(prev => ({ ...prev, imageFile: undefined, imageUrl: undefined }));
    }
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      description: '',
      originalPrice: '',
      discountedPrice: '',
      discountPercentage: '',
      category: '',
      usageFrequency: UsageFrequency.Unlimited,
      hasTimeRestriction: false,
      availableFrom: '',
      availableTo: '',
      imageFile: undefined,
      imageUrl: undefined,
    });
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const resetEditForm = () => {
    setEditForm({
      name: '',
      description: '',
      originalPrice: '',
      discountedPrice: '',
      discountPercentage: '',
      category: '',
      usageFrequency: UsageFrequency.Unlimited,
      isAvailable: true,
      hasTimeRestriction: false,
      availableFrom: '',
      availableTo: '',
      imageFile: undefined,
      imageUrl: undefined,
    });
  };
  */

  // Temporariamente comentado - não utilizado
  /*
  const handleEditOriginalPriceChange = (value: string) => {
    const originalPrice = parseFloat(value.replace(',', '.'));
    const discountPercentage = parseFloat(editForm.discountPercentage.replace(',', '.'));
    
    if (!isNaN(originalPrice) && !isNaN(discountPercentage)) {
      const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      setEditForm(prev => ({
        ...prev,
        originalPrice: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      }));
    } else {
      setEditForm(prev => ({ ...prev, originalPrice: value }));
    }
  };

  const handleEditDiscountPercentageChange = (value: string) => {
    const percentage = parseFloat(value.replace(',', '.'));
    const originalPrice = parseFloat(editForm.originalPrice.replace(',', '.'));
    
    if (!isNaN(percentage) && !isNaN(originalPrice)) {
      const discountedPrice = calculateDiscountedPrice(originalPrice, percentage);
      setEditForm(prev => ({
        ...prev,
        discountPercentage: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      }));
    } else {
      setEditForm(prev => ({ ...prev, discountPercentage: value }));
    }
  };
  */

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleUpdateProduct = async (form: any) => {
    if (!currentCafeteria || !selectedProduct) return;

    try {
      setIsUpdating(true);
      setError('');

      // Debug: verificar os dados recebidos do modal
      console.log('ProductsPage - Form received from modal:', form);
      console.log('ProductsPage - discountPercentage received:', form.discountPercentage);
      console.log('ProductsPage - discountPercentage type:', typeof form.discountPercentage);

      // Verificar se discountPercentage é válido
      if (!form.discountPercentage || form.discountPercentage === '') {
        console.error('ProductsPage - discountPercentage is empty or invalid:', form.discountPercentage);
        setError('Percentual de desconto é obrigatório');
        return;
      }

      // Preparar dados para envio
      const productData: any = {
        name: form.name,
        description: form.description,
        originalPrice: parseFloat(form.originalPrice.replace(',', '.')),
        discountPercentage: parseFloat(form.discountPercentage),
        discountedPrice: parseFloat(form.discountedPrice.replace(',', '.')), // Adicionar o preço com desconto
        category: form.category,
        usageFrequency: form.usageFrequency,
        isAvailable: form.isAvailable,
        hasTimeRestriction: form.hasTimeRestriction,
        availableFrom: form.hasTimeRestriction ? form.availableFrom : undefined,
        availableTo: form.hasTimeRestriction ? form.availableTo : undefined,
      };

      // Verificar se a conversão foi bem-sucedida
      if (isNaN(productData.discountPercentage)) {
        console.error('ProductsPage - Failed to parse discountPercentage:', form.discountPercentage);
        setError('Percentual de desconto inválido');
        return;
      }

      // Verificar se o preço com desconto foi calculado corretamente
      if (isNaN(productData.discountedPrice)) {
        console.error('ProductsPage - Failed to parse discountedPrice:', form.discountedPrice);
        setError('Preço com desconto inválido');
        return;
      }

      // Verificação final antes da chamada da API
      if (productData.discountPercentage < 0 || productData.discountPercentage > 100) {
        console.error('ProductsPage - discountPercentage out of range:', productData.discountPercentage);
        setError('Percentual de desconto deve ser entre 0 e 100');
        return;
      }

      console.log('ProductsPage - All validations passed, calling API with:', {
        discountPercentage: productData.discountPercentage,
        discountedPrice: productData.discountedPrice
      });

      // Debug: verificar os dados preparados para envio
      console.log('ProductsPage - Product data prepared for API:', productData);
      console.log('ProductsPage - discountPercentage after parseFloat:', productData.discountPercentage);

      // Se há nova imagem, fazer upload primeiro
      if (form.imageFile) {
        const formData = new FormData();
        formData.append('file', form.imageFile);
        const response = await imageService.uploadImage(formData);
        productData.imageUrl = response.url;
      }

      await productService.updateDiscountedProduct(currentCafeteria.id, selectedProduct.id, productData);
      
      // Recarregar produtos
      await loadProducts();
      
      // Fechar modal e resetar seleção
      setShowEditModal(false);
      setSelectedProduct(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar produto');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!currentCafeteria || !selectedProduct) return;

    try {
      setIsDeleting(true);
      setError('');

      await productService.deleteDiscountedProduct(currentCafeteria.id, selectedProduct.id);
      
      // Recarregar produtos
      await loadProducts();
      
      // Fechar modal e resetar seleção
      setShowDeleteModal(false);
      setSelectedProduct(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao remover produto');
    } finally {
      setIsDeleting(false);
    }
  };

  // Temporariamente comentado - não utilizado
  /*
  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // Remove segundos se houver
  };
  */

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // Remove segundos se houver
  };

  const getAvailabilityText = (product: Product) => {
    if (!product.isAvailable) return 'Indisponível';
    if (product.isRequiredProduct) return 'Sempre Disponível';
    if (product.hasTimeRestriction) {
      const from = formatTime(product.availableFrom);
      const to = formatTime(product.availableTo);
      return `${from} - ${to}`;
    }
    return 'Disponível';
  };

  if (!currentCafeteria) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Produtos com Desconto</h1>
          <p className="text-gray-600 mt-2">
            Selecione uma cafeteria para gerenciar os produtos com desconto
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Produtos com Desconto</h1>
          <p className="text-gray-600 mt-2">
            Gerencie os produtos com desconto da {currentCafeteria.name}
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setShowCreateModal(true);
          }}
        >
          + Novo Produto com Desconto
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="spinner w-8 h-8"></div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Lista de Produtos com Desconto
            </h3>
          </div>
          <div className="card-body">
            {products.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Nenhum produto com desconto cadastrado ainda.</p>
                <button 
                  className="btn btn-primary mt-4"
                  onClick={() => {
                    setShowCreateModal(true);
                  }}
                >
                  Adicionar Primeiro Produto com Desconto
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Imagem</th>
                        <th>Nome</th>
                        <th>Preço Original</th>
                        <th>Preço com Desconto</th>
                        <th>Desconto</th>
                        <th>Frequência de Uso</th>
                        <th>Disponibilidade</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.id}>
                          <td>
                            {product.imageUrl ? (
                              <img 
                                src={product.imageUrl} 
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </td>
                          <td>
                            <div>
                              <p className="font-medium text-gray-800">{product.name}</p>
                              {product.description && (
                                <p className="text-sm text-gray-600">{product.description}</p>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="text-gray-500 line-through">
                              R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                            </span>
                          </td>
                          <td>
                            {product.discountedPrice ? (
                              <span className="font-bold text-green-600 text-lg">
                                R$ {product.discountedPrice.toFixed(2).replace('.', ',')}
                              </span>
                            ) : (
                              <span className="text-gray-400">Sem desconto</span>
                            )}
                          </td>
                          <td>
                            {product.discountPercentage > 0 ? (
                              <span className="badge badge-success">
                                -{product.discountPercentage}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td>
                            <span className="badge badge-info">
                              {getUsageFrequencyLabel(product.usageFrequency || UsageFrequency.Unlimited)}
                            </span>
                          </td>
                          <td>
                            <span className={`text-sm ${product.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                              {getAvailabilityText(product)}
                            </span>
                          </td>
                          <td>
                            <div className="flex space-x-2">
                              <button 
                                  className={`btn btn-outline btn-sm ${product.isBlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  onClick={() => {
                                    if (!product.isBlocked) {
                                      handleEditProduct(product);
                                    }
                                  }}
                                  disabled={product.isBlocked}
                                  title={product.isBlocked ? 'Produto bloqueado não pode ser editado' : 'Editar produto'}
                                >
                                  Editar
                                </button>
                              <button 
                                className={`btn btn-outline btn-sm text-red-600 hover:bg-red-50 ${product.isBlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={() => {
                                  if (!product.isBlocked) {
                                    handleDeleteProduct(product);
                                  }
                                }}
                                disabled={product.isBlocked}
                                title={product.isBlocked ? 'Produto bloqueado não pode ser removido' : 'Remover produto'}
                              >
                                Remover
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {products.map((product) => (
                    <div key={product.id} className="bg-white rounded-lg border border-gray-200 p-4">
                      {/* Header do produto */}
                      <div className="flex items-start space-x-3 mb-3">
                        {product.imageUrl ? (
                          <img 
                            src={product.imageUrl} 
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-800 text-lg">{product.name}</h3>
                          {product.description && (
                            <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Preços */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 line-through text-sm">
                            R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                          </span>
                          {product.discountedPrice && (
                            <span className="font-bold text-green-600 text-lg">
                              R$ {product.discountedPrice.toFixed(2).replace('.', ',')}
                            </span>
                          )}
                        </div>
                        {product.discountPercentage > 0 && (
                          <span className="badge badge-success">
                            -{product.discountPercentage}%
                          </span>
                        )}
                      </div>

                      {/* Informações adicionais */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Frequência:</span>
                          <span className="badge badge-info text-xs">
                            {getUsageFrequencyLabel(product.usageFrequency || UsageFrequency.Unlimited)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Disponibilidade:</span>
                          <span className={`text-sm font-medium ${product.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                            {getAvailabilityText(product)}
                          </span>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex space-x-2">
                        <button 
                          className={`flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors flex-1 justify-center ${product.isBlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => {
                            if (!product.isBlocked) {
                              handleEditProduct(product);
                            }
                          }}
                          disabled={product.isBlocked}
                          title={product.isBlocked ? 'Produto bloqueado não pode ser editado' : 'Editar produto'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="text-sm font-medium">Editar</span>
                        </button>
                        <button 
                          className={`flex items-center space-x-1 px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors flex-1 justify-center ${product.isBlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => {
                            if (!product.isBlocked) {
                              handleDeleteProduct(product);
                            }
                          }}
                          disabled={product.isBlocked}
                          title={product.isBlocked ? 'Produto bloqueado não pode ser removido' : 'Remover produto'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-sm font-medium">Remover</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Criação de Produto com Desconto */}
      {showCreateModal && (
        <CreateProductModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
          }}
          onSubmit={handleCreateProduct}
          usageFrequencyOptions={usageFrequencyOptions}
          isCreating={isCreating}
          error={error}
          setError={setError}
        />
      )}

      {/* Modal de Edição de Produto */}
      {showEditModal && selectedProduct && (
        <EditProductModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          onSubmit={handleUpdateProduct}
          product={selectedProduct}
          usageFrequencyOptions={usageFrequencyOptions}
          isUpdating={isUpdating}
          error={error}
          setError={setError}
        />
      )}

      {/* Modal de Confirmação de Remoção */}
      {showDeleteModal && selectedProduct && (
        <DeleteProductModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedProduct(null);
          }}
          onConfirm={confirmDeleteProduct}
          productName={selectedProduct.name}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default ProductsPage; 